// Application state (Svelte 5 runes): the user, conversations, messages per conversation,
// threads, pinned and saved messages, presence and typing. Realtime events from lib/ws.ts
// land here.
//
// One message can be shown in several places at once (timeline, open thread, pinned list,
// saved list): every change goes through `patchMessage`, which updates all of them.
// Events carry the message as anyone sees it: `saved` stays as it was here, and `me` in
// reactions is recomputed from `user_ids`.
import { api, type Attachment, type Conversation, type DirectoryUser, type FileItem, type Info, type Me, type Message, type Reaction, type SavedItem, type TaskSummary, type Thread } from './api';
import { mentions } from './markdown';

export type Presence = 'online' | 'away' | 'offline';

export interface Timeline {
  items: Message[];
  /** Older messages exist on the server. */
  hasMore: boolean;
  /** Newer messages exist on the server: the page was opened around an older message. */
  hasNewer: boolean;
  loading: boolean;
  /** A message to scroll to and highlight (opened from saved, pinned or search). */
  focusId: string | null;
}

export interface ThreadState extends Thread {
  loading: boolean;
}

const PAGE = 50;
const TYPING_MS = 5000;

class AppState {
  info = $state<Info | null>(null);
  me = $state<Me | null>(null);
  conversations = $state<Conversation[]>([]);
  timelines = $state<Record<string, Timeline>>({});
  threads = $state<Record<string, ThreadState>>({});
  pinned = $state<Record<string, Message[]>>({});
  saved = $state<SavedItem[] | null>(null);
  presence = $state<Record<string, Presence>>({});
  /** Active accounts by id: names and pictures follow `user.updated` here. */
  users = $state<Record<string, DirectoryUser>>({});
  /** conversation id → user id → time the indicator expires */
  typing = $state<Record<string, Record<string, number>>>({});
  connected = $state(false);
  focused = $state(typeof document !== 'undefined' ? document.hasFocus() : true);
  /** Conversation on screen, if any. */
  openId = $state<string | null>(null);
  /** Phase 2: the board of each project opened, « Mes tâches », and its figures. */
  boards = $state<Record<string, Message[]>>({});
  myTasks = $state<{ my_turn: Message[]; waiting_others: Message[]; done: Message[] } | null>(null);
  taskSummary = $state<TaskSummary | null>(null);
  private tasksRefresh: ReturnType<typeof setTimeout> | null = null;
  /** A file shown in the right panel (« Aperçu » on a message). */
  preview = $state<FileItem | null>(null);

  unreadTotal = $derived(this.conversations.filter((c) => !c.archived_at && c.unread_count > 0).length);

  conversation(id: string | null | undefined): Conversation | undefined {
    return id ? this.conversations.find((c) => c.id === id) : undefined;
  }

  reset(): void {
    this.me = null;
    this.conversations = [];
    this.timelines = {};
    this.threads = {};
    this.pinned = {};
    this.saved = null;
    this.presence = {};
    this.users = {};
    this.typing = {};
    this.openId = null;
    this.preview = null;
    this.boards = {};
    this.myTasks = null;
    this.taskSummary = null;
  }

  /** Shows a message's file in the right panel. */
  showPreview(message: Message, file: Attachment): void {
    this.preview = {
      ...file,
      message_id: message.id,
      conversation_id: message.conversation_id,
      root_id: message.root_id,
      created_at: message.created_at,
      author: message.author,
    };
  }

  // --- Users ------------------------------------------------------------------------------

  async loadUsers(): Promise<void> {
    const list = await api.users();
    this.users = Object.fromEntries(list.map((u) => [u.id, u]));
  }

  /** The picture to show for a user: the directory's, which follows the changes. */
  avatarOf(user: { id: string; avatar_url?: string | null }): string | null {
    const known = this.users[user.id];
    return known ? known.avatar_url : (user.avatar_url ?? null);
  }

  /** A name or picture changed (`user.updated`, or one's own profile page). */
  applyUser(u: { id: string; display_name: string; avatar_url: string | null }): void {
    const known = this.users[u.id];
    if (known) this.users[u.id] = { ...known, display_name: u.display_name, avatar_url: u.avatar_url };
    if (this.me?.id === u.id) this.me = { ...this.me, display_name: u.display_name, avatar_url: u.avatar_url };
    const fix = (m: Message): Message =>
      m.author.id === u.id ? { ...m, author: { ...m.author, display_name: u.display_name, avatar_url: u.avatar_url } } : m;
    for (const t of Object.values(this.timelines)) if (t.items.some((m) => m.author.id === u.id)) t.items = t.items.map(fix);
    for (const t of Object.values(this.threads)) {
      t.root = fix(t.root);
      t.replies = t.replies.map(fix);
    }
    for (const key of Object.keys(this.pinned)) this.pinned[key] = this.pinned[key]!.map(fix);
    if (this.saved) this.saved = this.saved.map((s) => ({ ...s, message: fix(s.message) }));
    // Titles of direct messages and groups are made from the names: ask the server again.
    if (this.conversations.some((c) => c.members.some((m) => m.id === u.id))) void this.loadConversations().catch(() => {});
  }

  // --- Conversations ----------------------------------------------------------------------

  async loadConversations(): Promise<void> {
    this.conversations = sortConversations(await api.conversations());
  }

  upsertConversation(conversation: Conversation): void {
    const others = this.conversations.filter((c) => c.id !== conversation.id);
    this.conversations = sortConversations([...others, conversation]);
  }

  removeConversation(id: string): void {
    this.conversations = this.conversations.filter((c) => c.id !== id);
    delete this.timelines[id];
    delete this.pinned[id];
    if (this.saved) this.saved = this.saved.filter((s) => s.message.conversation_id !== id);
  }

  private patchConversation(id: string, change: Partial<Conversation>): void {
    this.conversations = sortConversations(
      this.conversations.map((c) => (c.id === id ? { ...c, ...change } : c)),
    );
  }

  // --- One message, wherever it is shown --------------------------------------------------

  /** `me` in reactions, from who reacted. */
  private withMe(reactions: Reaction[]): Reaction[] {
    const me = this.me?.id;
    return reactions.map((r) => ({ ...r, me: !!me && r.user_ids.includes(me) }));
  }

  /** A message received from an event: the user's own state is kept. */
  private fromEvent(message: Message, previous?: Message): Message {
    return { ...message, saved: previous?.saved ?? message.saved, reactions: this.withMe(message.reactions) };
  }

  private patchMessage(id: string, change: (m: Message) => Message): void {
    const apply = (list: Message[]) => (list.some((m) => m.id === id) ? list.map((m) => (m.id === id ? change(m) : m)) : list);
    for (const t of Object.values(this.timelines)) t.items = apply(t.items);
    for (const t of Object.values(this.threads)) {
      if (t.root.id === id) t.root = change(t.root);
      t.replies = apply(t.replies);
    }
    for (const key of Object.keys(this.pinned)) this.pinned[key] = apply(this.pinned[key]!);
    for (const key of Object.keys(this.boards)) this.boards[key] = apply(this.boards[key]!);
    if (this.myTasks) {
      this.myTasks = {
        my_turn: apply(this.myTasks.my_turn),
        waiting_others: apply(this.myTasks.waiting_others),
        done: apply(this.myTasks.done),
      };
    }
    if (this.saved) this.saved = this.saved.map((s) => (s.message.id === id ? { ...s, message: change(s.message) } : s));
  }

  updateMessage(message: Message): void {
    this.patchMessage(message.id, (old) => this.fromEvent(message, old));
    // The pinned list follows the pins.
    const list = this.pinned[message.conversation_id];
    if (list) {
      const present = list.some((m) => m.id === message.id);
      if (message.pinned && !present) this.pinned[message.conversation_id] = [this.fromEvent(message), ...list];
      if (!message.pinned && present) this.pinned[message.conversation_id] = list.filter((m) => m.id !== message.id);
    }
  }

  setReactions(messageId: string, reactions: Reaction[]): void {
    this.patchMessage(messageId, (m) => ({ ...m, reactions: this.withMe(reactions) }));
  }

  setSaved(messageId: string, saved: boolean): void {
    this.patchMessage(messageId, (m) => ({ ...m, saved }));
    if (this.saved && !saved) this.saved = this.saved.filter((s) => s.message.id !== messageId);
  }

  deleteMessage(id: string): void {
    if (this.preview?.message_id === id) this.preview = null;
    for (const key of Object.keys(this.boards)) {
      if (this.boards[key]!.some((m) => m.id === id)) {
        this.boards[key] = this.boards[key]!.filter((m) => m.id !== id);
        this.refreshMyTasks();
      }
    }
    this.patchMessage(id, (m) => ({ ...m, deleted: true, body: '', reactions: [], attachments: [], pinned: false, saved: false }));
    for (const key of Object.keys(this.pinned)) this.pinned[key] = this.pinned[key]!.filter((m) => m.id !== id);
    if (this.saved) this.saved = this.saved.filter((s) => s.message.id !== id);
  }

  // --- Timelines --------------------------------------------------------------------------

  private newTimeline(): Timeline {
    return { items: [], hasMore: true, hasNewer: false, loading: false, focusId: null };
  }

  timeline(id: string): Timeline {
    // Read back from the state: assigning returns the plain object, not the reactive proxy.
    if (!this.timelines[id]) this.timelines[id] = this.newTimeline();
    return this.timelines[id]!;
  }

  /** The latest page, when a conversation is opened (or when leaving an older page). */
  async loadLatest(id: string, force = false): Promise<void> {
    const t = this.timeline(id);
    if ((t.items.length > 0 && !force) || t.loading) return;
    t.loading = true;
    try {
      const items = await api.messages(id, undefined, PAGE);
      Object.assign(t, { items, hasMore: items.length === PAGE, hasNewer: false, focusId: null });
    } finally {
      t.loading = false;
    }
  }

  /** A page around a message, to show it in its context. */
  async jumpTo(id: string, messageId: string): Promise<void> {
    const t = this.timeline(id);
    t.loading = true;
    try {
      const items = await api.messages(id, { around: messageId }, PAGE);
      // The server sends PAGE/2 older messages, the anchor, then the newer ones: fewer
      // means the start or the end of the conversation is on this page.
      const older = Math.floor(PAGE / 2);
      const index = items.findIndex((m) => m.id === messageId);
      const at = index >= 0 ? index : Math.min(older, items.length - 1);
      Object.assign(t, {
        items,
        hasMore: at >= older,
        hasNewer: items.length - at - 1 >= PAGE - older - 1,
        focusId: items[at]?.id ?? null,
      });
    } finally {
      t.loading = false;
    }
  }

  /** The page before the oldest message shown; false when there is nothing more. */
  async loadOlder(id: string): Promise<boolean> {
    const t = this.timeline(id);
    const oldest = t.items[0];
    if (!t.hasMore || t.loading || !oldest) return false;
    t.loading = true;
    try {
      const items = await api.messages(id, { before: oldest.id }, PAGE);
      t.items = [...items, ...t.items];
      t.hasMore = items.length === PAGE;
      return items.length > 0;
    } finally {
      t.loading = false;
    }
  }

  /** The page after the newest message shown, when the page was opened around an older one. */
  async loadNewer(id: string): Promise<boolean> {
    const t = this.timeline(id);
    const newest = t.items.at(-1);
    if (!t.hasNewer || t.loading || !newest) return false;
    t.loading = true;
    try {
      const items = await api.messages(id, { after: newest.id }, PAGE);
      t.items = [...t.items, ...items];
      t.hasNewer = items.length === PAGE;
      // Back to the live end: new messages are followed again.
      if (!t.hasNewer) t.focusId = null;
      return items.length > 0;
    } finally {
      t.loading = false;
    }
  }

  /** After a reconnection: what was posted while the socket was down. */
  async catchUp(id: string): Promise<void> {
    const t = this.timelines[id];
    if (!t || t.hasNewer || !t.items.at(-1)) return;
    for (;;) {
      const items = await api.messages(id, { after: t.items.at(-1)!.id }, 100);
      items.forEach((m) => this.addMessage(m, false));
      if (items.length < 100) break;
    }
  }

  /** A new message, from the API reply or the socket: added once, in order. */
  addMessage(message: Message, fromSocket = true): void {
    if (message.root_id) {
      const thread = this.threads[message.root_id];
      if (thread && !thread.replies.some((m) => m.id === message.id)) {
        thread.replies = [...thread.replies, this.fromEvent(message)];
      }
      this.stopTyping(message.conversation_id, message.author.id);
      return;
    }
    const t = this.timelines[message.conversation_id];
    // On an older page, the newest messages are not loaded: adding one would leave a gap.
    if (t && !t.hasNewer && !t.items.some((m) => m.id === message.id)) {
      const items = [...t.items, this.fromEvent(message)];
      items.sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
      t.items = items;
    }
    const conversation = this.conversation(message.conversation_id);
    if (!conversation || message.kind === 'system') return;
    const change: Partial<Conversation> = {};
    if (!conversation.last_message_at || message.created_at > conversation.last_message_at) {
      change.last_message_at = message.created_at;
    }
    const mine = message.author.id === this.me?.id;
    if (mine) {
      change.last_read_at = message.created_at;
      change.unread_count = 0;
      change.mention_count = 0;
    } else if (fromSocket && message.created_at > conversation.last_read_at) {
      change.unread_count = Math.min(99, conversation.unread_count + 1);
      if (this.me && mentions(message.body, this.me.username)) {
        change.mention_count = Math.min(99, conversation.mention_count + 1);
      }
    }
    this.patchConversation(message.conversation_id, change);
    // Whoever writes has stopped typing.
    this.stopTyping(message.conversation_id, message.author.id);
  }

  /** Read up to this message: on the server, then here. */
  async markRead(id: string, message: Message): Promise<void> {
    const conversation = this.conversation(id);
    if (!conversation || message.created_at <= conversation.last_read_at) return;
    this.patchConversation(id, { last_read_at: message.created_at, unread_count: 0, mention_count: 0 });
    await api.markRead(id, message.id);
  }

  async readElsewhere(id: string): Promise<void> {
    this.upsertConversation(await api.conversation(id));
  }

  // --- Threads, pinned, saved -------------------------------------------------------------

  async loadThread(rootId: string): Promise<void> {
    const current = this.threads[rootId];
    if (current?.loading) return;
    const thread = await api.thread(rootId);
    this.threads[thread.root.id] = { ...thread, loading: false };
  }

  async loadPinned(conversationId: string): Promise<void> {
    this.pinned[conversationId] = await api.pinned(conversationId);
  }

  async loadSaved(more = false): Promise<boolean> {
    const before = more ? this.saved?.at(-1)?.message.id : undefined;
    const items = await api.saved(before);
    this.saved = more && this.saved ? [...this.saved, ...items] : items;
    return items.length > 0;
  }

  async toggleSaved(message: Message): Promise<void> {
    const on = !message.saved;
    this.setSaved(message.id, on);
    try {
      await api.save(message.id, on);
      if (on && this.saved) await this.loadSaved();
    } catch (e) {
      this.setSaved(message.id, !on);
      throw e;
    }
  }

  async togglePin(message: Message): Promise<void> {
    this.updateMessage(await api.pin(message.id, !message.pinned));
  }

  async toggleReaction(message: Message, emoji: string): Promise<void> {
    const on = !message.reactions.some((r) => r.emoji === emoji && r.me);
    const result = await api.react(message.id, emoji, on);
    this.setReactions(message.id, result.reactions);
  }

  async edit(message: Message, body: string): Promise<void> {
    this.updateMessage(await api.edit(message.id, body));
  }

  async remove(message: Message): Promise<void> {
    await api.remove(message.id);
    this.deleteMessage(message.id);
  }

  // --- Tasks (phase 2) ---------------------------------------------------------------------

  async loadBoard(conversationId: string): Promise<void> {
    this.boards[conversationId] = await api.board(conversationId);
  }

  async loadMyTasks(): Promise<void> {
    const [my_turn, waiting_others, done] = await Promise.all([
      api.myTasks('my_turn'),
      api.myTasks('waiting_others'),
      api.myTasks('done'),
    ]);
    this.myTasks = { my_turn, waiting_others, done };
  }

  async loadTaskSummary(): Promise<void> {
    this.taskSummary = await api.taskSummary();
  }

  /** « Mes tâches » and its figures follow the changes, a moment after them. */
  private refreshMyTasks(): void {
    if (this.tasksRefresh) clearTimeout(this.tasksRefresh);
    this.tasksRefresh = setTimeout(() => {
      void this.loadTaskSummary().catch(() => {});
      if (this.myTasks) void this.loadMyTasks().catch(() => {});
    }, 300);
  }

  /** `task.upserted`, or the answer of a task route: the message everywhere, the board. */
  upsertTask(message: Message): void {
    this.updateMessage(message);
    const board = this.boards[message.conversation_id];
    if (board) {
      const fresh = this.fromEvent(message, board.find((m) => m.id === message.id));
      const others = board.filter((m) => m.id !== message.id);
      this.boards[message.conversation_id] = [...others, fresh].sort(
        (a, b) => (a.task?.position ?? 0) - (b.task?.position ?? 0) || a.id.localeCompare(b.id),
      );
    }
    this.refreshMyTasks();
  }

  // --- Typing -----------------------------------------------------------------------------

  startTyping(conversationId: string, userId: string): void {
    const now = Date.now();
    const current = this.typing[conversationId] ?? {};
    this.typing[conversationId] = { ...current, [userId]: now + TYPING_MS };
    setTimeout(() => this.expireTyping(conversationId), TYPING_MS + 50);
  }

  private stopTyping(conversationId: string, userId: string): void {
    const current = this.typing[conversationId];
    if (current?.[userId]) {
      const { [userId]: _gone, ...rest } = current;
      this.typing[conversationId] = rest;
    }
  }

  private expireTyping(conversationId: string): void {
    const now = Date.now();
    const current = this.typing[conversationId] ?? {};
    this.typing[conversationId] = Object.fromEntries(Object.entries(current).filter(([, until]) => until > now));
  }

  typingNames(conversationId: string): string[] {
    const ids = Object.keys(this.typing[conversationId] ?? {});
    const conversation = this.conversation(conversationId);
    return ids
      .map((id) => conversation?.members.find((m) => m.id === id)?.display_name)
      .filter((name): name is string => !!name);
  }
}

function sortConversations(list: Conversation[]): Conversation[] {
  return [...list].sort(
    (a, b) =>
      (b.last_message_at ?? b.created_at).localeCompare(a.last_message_at ?? a.created_at) ||
      a.title.localeCompare(b.title, 'fr'),
  );
}

export const app = new AppState();
